import { getNodeInputs, type ActionGraph, type ActionNode } from "../actions.ts";
import type { Condition } from "../conditions.ts";
import type { ProjectView } from "../entities.ts";
import { getContextScopeSpec } from "../contextRegistry.ts";
import { ACTION_SPECS, type ActionSpec } from "../schemaSpec.ts";
import {
	EVENT_TYPE_SET,
	formatPath,
	issue,
	pushBrokenRef,
} from "./helpers.ts";
import type { Issue, IssuePath, ValidationReport } from "./types.ts";

type DataPortSpec = { name: string; kind: string };

const ACTION_SPEC_BY_NAME: Record<string, ActionSpec> = Object.fromEntries(
	ACTION_SPECS.map((spec) => [spec.type, spec]),
);

const findPort = (
	ports: readonly DataPortSpec[] | undefined,
	name: string,
): DataPortSpec | undefined => ports?.find((port) => port.name === name);

const splitSourcePortPath = (
	sourcePortName: string,
): { basePort: string; hasPath: boolean } => {
	const dotIndex = sourcePortName.indexOf(".");
	if (dotIndex < 0) return { basePort: sourcePortName, hasPath: false };
	return {
		basePort: sourcePortName.slice(0, dotIndex),
		hasPath: sourcePortName.slice(dotIndex + 1).length > 0,
	};
};

const pushEntityBrokenRef = (
	report: ValidationReport,
	entityKind: Issue["entityKind"],
	entityId: string,
	fieldPath: string,
): void => {
	if (
		entityKind === "items" ||
		entityKind === "attacks" ||
		entityKind === "recipes" ||
		entityKind === "currencies" ||
		entityKind === "stats" ||
		entityKind === "quests"
	) {
		pushBrokenRef(report, entityKind, entityId, fieldPath);
	}
};

const getDeclaredSourcePort = (
	sourceNode: ActionNode,
	sourcePortName: string,
): DataPortSpec | undefined => {
	const { basePort, hasPath } = splitSourcePortPath(sourcePortName);
	const sourceSpec = ACTION_SPEC_BY_NAME[sourceNode.type];
	const declaredPort = findPort(sourceSpec?.dataOutputs, sourcePortName);
	if (declaredPort) return declaredPort;
	const declaredBasePort = hasPath
		? findPort(sourceSpec?.dataOutputs, basePort)
		: undefined;
	if (declaredBasePort) {
		return hasPath
			? { name: sourcePortName, kind: "any" }
			: declaredBasePort;
	}

	if (sourceNode.type === "Get") {
		const query = (sourceNode as unknown as { query?: unknown }).query;
		if (typeof query === "object" && query !== null && !Array.isArray(query)) {
			const q = query as { source?: unknown; arg?: unknown };
			if (q.source === "Context" && typeof q.arg === "string") {
				const scopeSpec = getContextScopeSpec(q.arg);
				if (sourcePortName === "value" || basePort === "value") {
					return { name: sourcePortName, kind: "any" };
				}
				const field = scopeSpec?.fields.find((entry) => entry.name === sourcePortName);
				if (field) return field;
				if (hasPath) {
					const baseField = scopeSpec?.fields.find((entry) => entry.name === basePort);
					if (baseField) return { name: sourcePortName, kind: "any" };
				}
			}
		}
	}

	if (sourceNode.type === "Switch") {
		if (sourcePortName === "Default") {
			return { name: "Default", kind: "any" };
		}
		const cases = (sourceNode as unknown as { cases?: unknown }).cases;
		if (Array.isArray(cases)) {
			for (const c of cases) {
				if (typeof c !== "object" || c === null || Array.isArray(c)) continue;
				const value = (c as { value?: unknown }).value;
				if (value !== undefined && String(value) === sourcePortName) {
					return { name: sourcePortName, kind: "any" };
				}
			}
		}
	}

	return undefined;
};

export const validateCondition = (
	cond: Condition,
	view: ProjectView,
	entityId: string,
	basePath: IssuePath,
	report: ValidationReport,
	actionType: string,
	nodeId: string,
	entityKind: Issue["entityKind"] = "items",
): void => {
	if (cond.type === "HasItem") {
		if (!view.items[cond.itemId]) {
			report.errors.push(
				issue(
					"error",
					entityKind,
					entityId,
					[...basePath, "itemId"],
					`References unknown item '${cond.itemId}'`,
					"broken_ref",
					{ nodeId, actionType, fieldName: "conditions.itemId", refKind: "items", refId: cond.itemId },
				),
			);
			pushEntityBrokenRef(report, entityKind, entityId, formatPath([...basePath, "itemId"]));
		}
	} else if (cond.type === "HasCurrency") {
		if (!view.currencies[cond.currencyId]) {
			report.errors.push(
				issue(
					"error",
					entityKind,
					entityId,
					[...basePath, "currencyId"],
					`References unknown currency '${cond.currencyId}'`,
					"broken_ref",
					{ nodeId, actionType, fieldName: "conditions.currencyId", refKind: "currencies", refId: cond.currencyId },
				),
			);
			pushEntityBrokenRef(report, entityKind, entityId, formatPath([...basePath, "currencyId"]));
		}
	} else if (cond.type === "HasStat") {
		if (!view.stats[cond.statKey]) {
			report.errors.push(
				issue(
					"error",
					entityKind,
					entityId,
					[...basePath, "statKey"],
					`References unknown stat '${cond.statKey}'`,
					"broken_ref",
					{ nodeId, actionType, fieldName: "conditions.statKey", refKind: "stats", refId: cond.statKey },
				),
			);
			pushEntityBrokenRef(report, entityKind, entityId, formatPath([...basePath, "statKey"]));
		}
	} else if (cond.type === "IsOnZone") {
		const zoneItem = view.items[cond.zoneItemId];
		if (!zoneItem) {
			report.errors.push(
				issue(
					"error",
					entityKind,
					entityId,
					[...basePath, "zoneItemId"],
					`References unknown item '${cond.zoneItemId}'`,
					"broken_ref",
					{ nodeId, actionType, fieldName: "conditions.zoneItemId", refKind: "items", refId: cond.zoneItemId },
				),
			);
			pushEntityBrokenRef(report, entityKind, entityId, formatPath([...basePath, "zoneItemId"]));
		} else if (zoneItem.category !== "Zone") {
			report.errors.push(
				issue(
					"error",
					entityKind,
					entityId,
					[...basePath, "zoneItemId"],
					`Condition IsOnZone expects a Zone item, got '${zoneItem.category ?? "none"}'`,
					"invalid_field",
					{ nodeId, actionType, fieldName: "conditions.zoneItemId", refKind: "items", refId: cond.zoneItemId },
				),
			);
		}
	}
};

export const validateNode = (
	node: ActionNode,
	view: ProjectView,
	entityId: string,
	report: ValidationReport,
	entityKind: Issue["entityKind"] = "items",
): void => {
	const nodeId = node.id;
	const t = node.type as string;
	const p: IssuePath = ["graph", "nodes", nodeId];
	const a = node as unknown as Record<string, unknown>;

	if (EVENT_TYPE_SET.has(t)) return;

	if (t === "ModifyItem") {
		const itemId = a.itemId as string | undefined;
		if (itemId && !view.items[itemId]) {
			report.errors.push(
				issue("error", entityKind, entityId, [...p, "itemId"], `ModifyItem references unknown item '${itemId}'`, "broken_ref", {
					nodeId, actionType: t, fieldName: "itemId", refKind: "items", refId: itemId,
				}),
			);
			pushEntityBrokenRef(report, entityKind, entityId, formatPath([...p, "itemId"]));
		}
	} else if (t === "AddRandomItem") {
		const entries = (a.entries as Array<Record<string, unknown>>) ?? [];
		entries.forEach((e, j) => {
			const eid = e.itemId as string | undefined;
			if (eid && !view.items[eid]) {
				report.errors.push(
					issue("error", entityKind, entityId, [...p, "entries", j, "itemId"], `References unknown item '${eid}'`, "broken_ref", {
						nodeId, actionType: t, fieldName: `entries[${j}].itemId`, refKind: "items", refId: eid,
					}),
				);
				pushEntityBrokenRef(report, entityKind, entityId, formatPath([...p, "entries", j, "itemId"]));
			}
		});
	} else if (t === "ExecuteTransaction") {
		const recipeId = a.recipeId as string | undefined;
		if (recipeId && !view.recipes[recipeId]) {
			report.errors.push(
				issue("error", entityKind, entityId, [...p, "recipeId"], `ExecuteTransaction references unknown recipe '${recipeId}'`, "broken_ref", {
					nodeId, actionType: t, fieldName: "recipeId", refKind: "recipes", refId: recipeId,
				}),
			);
			pushEntityBrokenRef(report, entityKind, entityId, formatPath([...p, "recipeId"]));
		}
	} else if (t === "ModifyCurrency") {
		const cid = a.currencyId as string | undefined;
		if (cid && !view.currencies[cid]) {
			report.errors.push(
				issue("error", entityKind, entityId, [...p, "currencyId"], `ModifyCurrency references unknown currency '${cid}'`, "broken_ref", {
					nodeId, actionType: t, fieldName: "currencyId", refKind: "currencies", refId: cid,
				}),
			);
			pushEntityBrokenRef(report, entityKind, entityId, formatPath([...p, "currencyId"]));
		}
	} else if (t === "OnProximity") {
		const itemIds = a.itemIds as string[] | undefined;
		if (Array.isArray(itemIds)) {
			itemIds.forEach((itemId, j) => {
				if (typeof itemId === "string" && itemId && !view.items[itemId]) {
					report.errors.push(
						issue("error", entityKind, entityId, [...p, "itemIds", j], `OnProximity references unknown item '${itemId}'`, "broken_ref", {
							nodeId, actionType: t, fieldName: `itemIds[${j}]`, refKind: "items", refId: itemId,
						}),
					);
					pushEntityBrokenRef(report, entityKind, entityId, formatPath([...p, "itemIds", j]));
				}
			});
		}
	} else if (t === "ChangeBehavior") {
		const targetItemId = a.targetItemId as string | undefined;
		if (typeof targetItemId === "string" && targetItemId && !view.items[targetItemId]) {
			report.errors.push(
				issue("error", entityKind, entityId, [...p, "targetItemId"], `ChangeBehavior references unknown target item '${targetItemId}'`, "broken_ref", {
					nodeId, actionType: t, fieldName: "targetItemId", refKind: "items", refId: targetItemId,
				}),
			);
			pushEntityBrokenRef(report, entityKind, entityId, formatPath([...p, "targetItemId"]));
		}
		const targetItemIds = a.targetItemIds as string[] | undefined;
		if (Array.isArray(targetItemIds)) {
			targetItemIds.forEach((itemId, j) => {
				if (typeof itemId === "string" && itemId && !view.items[itemId]) {
					report.errors.push(
						issue("error", entityKind, entityId, [...p, "targetItemIds", j], `ChangeBehavior references unknown target item '${itemId}'`, "broken_ref", {
							nodeId, actionType: t, fieldName: `targetItemIds[${j}]`, refKind: "items", refId: itemId,
						}),
					);
					pushEntityBrokenRef(report, entityKind, entityId, formatPath([...p, "targetItemIds", j]));
				}
			});
		}
	} else if (t === "Transform") {
		const recipes = (a.recipes as string[]) ?? [];
		recipes.forEach((rid, j) => {
			if (!view.recipes[rid]) {
				report.errors.push(
					issue("error", entityKind, entityId, [...p, "recipes", j], `Transform references unknown recipe '${rid}'`, "broken_ref", {
						nodeId, actionType: t, fieldName: `recipes[${j}]`, refKind: "recipes", refId: rid,
					}),
				);
				pushEntityBrokenRef(report, entityKind, entityId, formatPath([...p, "recipes", j]));
			}
		});
	} else if (t === "ModifyStat") {
		const sk = a.statKey as string | undefined;
		if (sk && !view.stats[sk]) {
			report.errors.push(
				issue("error", entityKind, entityId, [...p, "statKey"], `${t} references unknown stat '${sk}'`, "broken_ref", {
					nodeId, actionType: t, fieldName: "statKey", refKind: "stats", refId: sk,
				}),
			);
			pushEntityBrokenRef(report, entityKind, entityId, formatPath([...p, "statKey"]));
		}
	} else if (t === "SetGoal") {
		const tid = a.targetItemId as string | undefined;
		if (tid && !view.items[tid]) {
			report.errors.push(
				issue("error", entityKind, entityId, [...p, "targetItemId"], `SetGoal references unknown item '${tid}'`, "broken_ref", {
					nodeId, actionType: t, fieldName: "targetItemId", refKind: "items", refId: tid,
				}),
			);
			pushEntityBrokenRef(report, entityKind, entityId, formatPath([...p, "targetItemId"]));
		}
	} else if (t === "Spawn") {
		const list = (a.contentList as string[]) ?? [];
		list.forEach((cid, j) => {
			if (!view.items[cid]) {
				report.errors.push(
					issue("error", entityKind, entityId, [...p, "contentList", j], `Spawn references unknown item '${cid}'`, "broken_ref", {
						nodeId, actionType: t, fieldName: `contentList[${j}]`, refKind: "items", refId: cid,
					}),
				);
				pushEntityBrokenRef(report, entityKind, entityId, formatPath([...p, "contentList", j]));
			}
		});
	} else if (t === "GiveQuest" || t === "CheckQuestStatus") {
		const questIds = a.questIds as string[] | undefined;
		if (Array.isArray(questIds)) {
			questIds.forEach((qid, j) => {
				if (typeof qid === "string" && qid && !view.quests[qid]) {
					report.errors.push(
						issue("error", entityKind, entityId, [...p, "questIds", j], `${t} references unknown quest '${qid}'`, "broken_ref", {
							nodeId, actionType: t, fieldName: `questIds[${j}]`, refKind: "quests", refId: qid,
						}),
					);
					pushEntityBrokenRef(report, entityKind, entityId, formatPath([...p, "questIds", j]));
				}
			});
		}
	} else if (t === "Branch" || t === "WaitUntil") {
		const conditions = a.conditions as Condition[] | undefined;
		if (Array.isArray(conditions)) {
			conditions.forEach((cond, ci) => {
				validateCondition(
					cond,
					view,
					entityId,
					[...p, "conditions", ci],
					report,
					t,
					nodeId,
					entityKind,
				);
			});
		}
	} else if (t === "Switch") {

		const cases = a.cases as Array<{ value?: unknown }> | undefined;
		if (!Array.isArray(cases) || cases.length === 0) {
			report.errors.push(
				issue("error", entityKind, entityId, [...p, "cases"],
					`Switch needs at least one case — add a value to match in the editor`, "missing_field",
					{ nodeId, actionType: t, fieldName: "cases" }),
			);
		} else {
			cases.forEach((c, ci) => {
				const v = c?.value;
				const isPrimitive =
					typeof v === "string" || typeof v === "number" || typeof v === "boolean";
				if (!isPrimitive) {
					report.errors.push(
						issue("error", entityKind, entityId, [...p, "cases", ci, "value"],
							`Switch case #${ci + 1} needs a primitive value (text, number or yes/no)`, "missing_field",
							{ nodeId, actionType: t, fieldName: `cases[${ci}].value` }),
					);
				}
			});
		}
	} else if (t === "Choice") {
		const prompt = a.prompt as string | undefined;
		if (!prompt) {
			report.errors.push(
				issue("error", entityKind, entityId, [...p, "prompt"],
					`Choice needs a prompt — write the question shown to the player`, "missing_field",
					{ nodeId, actionType: t, fieldName: "prompt" }),
			);
		}
		const cases = a.cases as Array<{ label?: unknown; handle?: unknown }> | undefined;
		if (!Array.isArray(cases) || cases.length === 0) {
			report.errors.push(
				issue("error", entityKind, entityId, [...p, "cases"],
					`Choice needs at least one response — add one in the editor`, "missing_field",
					{ nodeId, actionType: t, fieldName: "cases" }),
			);
		} else {
			const seen = new Set<string>();
			cases.forEach((c, ci) => {
				const label = typeof c?.label === "string" ? c.label : "";
				if (!label) {
					report.errors.push(
						issue("error", entityKind, entityId, [...p, "cases", ci, "label"],
							`Choice response #${ci + 1} needs a label`, "missing_field",
							{ nodeId, actionType: t, fieldName: `cases[${ci}].label` }),
					);
				}
				const handle = typeof c?.handle === "string" ? c.handle : "";
				if (!handle) {
					report.errors.push(
						issue("error", entityKind, entityId, [...p, "cases", ci, "handle"],
							`Choice response #${ci + 1} needs a handle name`, "missing_field",
							{ nodeId, actionType: t, fieldName: `cases[${ci}].handle` }),
					);
				} else if (handle === "Default") {
					report.errors.push(
						issue("error", entityKind, entityId, [...p, "cases", ci, "handle"],
							`Choice response #${ci + 1}: "Default" is reserved — rename this handle`, "invalid_value",
							{ nodeId, actionType: t, fieldName: `cases[${ci}].handle` }),
					);
				} else if (seen.has(handle)) {
					report.errors.push(
						issue("error", entityKind, entityId, [...p, "cases", ci, "handle"],
							`Choice response #${ci + 1}: duplicate handle "${handle}"`, "invalid_value",
							{ nodeId, actionType: t, fieldName: `cases[${ci}].handle` }),
					);
				}
				seen.add(handle);
			});
		}
	} else if (t === "PlaySound") {
		const soundPath = a.soundPath as string | undefined;
		if (!soundPath) {
			report.warnings.push(
				issue("warning", entityKind, entityId, [...p, "soundPath"],
					`PlaySound has no sound path — link a sound from the Studio plugin before shipping`, "missing_field",
					{ nodeId, actionType: t, fieldName: "soundPath" }),
			);
		}
	} else if (t === "PlayAnimation") {
		const target = a.target as string | undefined;
		if (target !== undefined && target.trim() === "") {
			report.errors.push(
				issue("error", entityKind, entityId, [...p, "target"],
					"PlayAnimation target must be a non-empty target expression", "invalid_value",
					{ nodeId, actionType: t, fieldName: "target" }),
			);
		}
		const animationPath = a.animationPath as string | undefined;
		if (!animationPath) {
			report.warnings.push(
				issue("warning", entityKind, entityId, [...p, "animationPath"],
					`PlayAnimation has no animation path — link an animation from the Studio plugin before shipping`, "missing_field",
					{ nodeId, actionType: t, fieldName: "animationPath" }),
			);
		}
	} else if (t === "ShowTitle") {
		const text = a.text as string | undefined;
		if (!text) {
			report.errors.push(
				issue("error", entityKind, entityId, [...p, "text"],
					`ShowTitle needs a title text`, "missing_field",
					{ nodeId, actionType: t, fieldName: "text" }),
			);
		}
	} else if (t === "Teleport") {
		const destination = a.destination as string | undefined;
		if (!destination) {
			report.warnings.push(
				issue("warning", entityKind, entityId, [...p, "destination"],
					`Teleport has no destination — link a spawn from the Studio plugin before shipping`, "missing_field",
					{ nodeId, actionType: t, fieldName: "destination" }),
			);
		}
	}
};

export const validateNodeInputs = (
	graph: ActionGraph,
	entityId: string,
	report: ValidationReport,
	entityKind: Issue["entityKind"] = "items",
): void => {
	for (const [nodeId, node] of Object.entries(graph.nodes)) {
		const inputs = getNodeInputs(node);
		if (!inputs) continue;
		const entries = Object.entries(inputs);
		if (entries.length === 0) continue;

		if (EVENT_TYPE_SET.has(node.type)) {
			report.errors.push(
				issue("error", entityKind, entityId,
					["graph", "nodes", nodeId, "inputs"],
					`EntryPoint '${nodeId}' cannot declare data inputs — entry points are flow-only`,
					"invalid_handle",
					{ nodeId }),
			);
			continue;
		}
		const targetSpec = ACTION_SPEC_BY_NAME[node.type];
		for (const [port, binding] of entries) {
			const sourceNode = graph.nodes[binding.sourceNodeId];
			if (!sourceNode) {
				report.errors.push(
					issue("error", entityKind, entityId,
						["graph", "nodes", nodeId, "inputs", port, "sourceNodeId"],
						`Data binding on '${nodeId}.${port}' references unknown source node '${binding.sourceNodeId}'`,
						"orphan_edge",
						{ nodeId, fieldName: port }),
				);
				continue;
			}

			const sourcePort = getDeclaredSourcePort(sourceNode, binding.sourcePort);
			if (!sourcePort) {
				report.warnings.push(
					issue("warning", entityKind, entityId,
						["graph", "nodes", nodeId, "inputs", port, "sourcePort"],
						`Data binding on '${nodeId}.${port}' reads '${sourceNode.type}.${binding.sourcePort}', which is not a declared dataOutput — binding ignored at runtime`,
						"data_binding_ignored",
						{ nodeId, fieldName: port }),
				);
			}

			const targetPort = findPort(targetSpec?.dataInputs, port);
			if (!targetPort) {
				report.warnings.push(
					issue("warning", entityKind, entityId,
						["graph", "nodes", nodeId, "inputs", port],
						`Data binding on '${nodeId}.${port}' targets '${node.type}', which does not declare a dataInput named '${port}' — binding ignored at runtime`,
						"data_binding_ignored",
						{ nodeId, actionType: node.type, fieldName: port }),
				);
			}

			if (sourcePort && targetPort) {
				const sourceKind = sourcePort.kind;
				const targetKind = targetPort.kind;
				if (sourceKind !== targetKind && sourceKind !== "any" && targetKind !== "any") {
					report.warnings.push(
						issue("warning", entityKind, entityId,
							["graph", "nodes", nodeId, "inputs", port],
							`Data binding on '${nodeId}.${port}' has a type mismatch — '${sourceNode.type}.${binding.sourcePort}' is '${sourceKind}' but '${node.type}.${port}' expects '${targetKind}'`,
							"data_binding_type_mismatch",
							{ nodeId, actionType: node.type, fieldName: port }),
					);
				}
			}
		}
	}
};
